import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, RefreshCw, Copy, Check, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';
import { icalAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const PLATFORMS = [
  { value: 'airbnb', label: 'Airbnb', color: '#FF5A5F', icon: '🏠' },
  { value: 'bookingcom', label: 'Booking.com', color: '#003580', icon: '🔵' },
  { value: 'vrbo', label: 'VRBO', color: '#0D47A1', icon: '🌴' },
  { value: 'google', label: 'Google Calendar', color: '#4285F4', icon: '📅' },
  { value: 'custom', label: 'Custom', color: '#6B7280', icon: '🔗' }
];

export default function ICalSyncManager({ propertyId }) {
  const [syncs, setSyncs] = useState({ exportUrl: null, imports: [] });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newFeed, setNewFeed] = useState({ platform: 'airbnb', name: '', icalUrl: '' });

  useEffect(() => {
    fetchSyncs();
  }, [propertyId]);

  const fetchSyncs = async () => {
    try {
      setLoading(true);
      const res = await icalAPI.getPropertySyncs(propertyId);
      setSyncs(res.data.data);
    } catch (error) {
      toast.error('Failed to load calendar syncs');
    } finally {
      setLoading(false);
    }
  };

  const generateExportUrl = async () => {
    try {
      const res = await icalAPI.generateExportUrl(propertyId);
      setSyncs(prev => ({ ...prev, exportUrl: res.data.data.exportUrl }));
      toast.success('Export URL generated!');
    } catch (error) {
      toast.error('Failed to generate export URL');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddFeed = async (e) => {
    e.preventDefault();
    try {
      await icalAPI.addImportFeed(propertyId, newFeed);
      toast.success('Calendar feed added successfully!');
      setShowAddModal(false);
      setNewFeed({ platform: 'airbnb', name: '', icalUrl: '' });
      fetchSyncs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add calendar feed');
    }
  };

  const handleRemoveFeed = async (feedId) => {
    if (!window.confirm('Are you sure you want to remove this calendar feed?')) return;
    try {
      await icalAPI.removeImportFeed(propertyId, feedId);
      toast.success('Calendar feed removed');
      fetchSyncs();
    } catch (error) {
      toast.error('Failed to remove calendar feed');
    }
  };

  const handleToggleSync = async (feedId, currentState) => {
    try {
      await icalAPI.toggleSyncFeed(propertyId, feedId, !currentState);
      toast.success(`Sync ${!currentState ? 'enabled' : 'disabled'}`);
      fetchSyncs();
    } catch (error) {
      toast.error('Failed to toggle sync');
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      const res = await icalAPI.syncProperty(propertyId);
      const results = res.data.data;
      const totalImported = results.reduce((sum, r) => sum + (r.imported || 0), 0);
      const totalUpdated = results.reduce((sum, r) => sum + (r.updated || 0), 0);
      toast.success(`Sync complete! ${totalImported} imported, ${totalUpdated} updated`);
      fetchSyncs();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Export Calendar */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📤 Export Calendar (Share with OTAs)</span>
        </div>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
          Share this calendar URL with Airbnb, Booking.com, VRBO to sync your availability automatically.
        </p>
        {syncs.exportUrl ? (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                className="form-input"
                value={syncs.exportUrl}
                readOnly
                style={{ flex: 1, fontSize: 12, fontFamily: 'monospace' }}
              />
              <button
                className="btn btn-secondary"
                onClick={() => copyToClipboard(syncs.exportUrl)}
                style={{ minWidth: 100 }}
              >
                {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy</>}
              </button>
            </div>
            <div style={{ padding: 12, background: '#F0FDF4', borderRadius: 8, fontSize: 12, color: '#166534' }}>
              <strong>How to use:</strong>
              <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                <li><strong>Airbnb:</strong> Calendar Settings → Import Calendar → Paste URL</li>
                <li><strong>Booking.com:</strong> Calendar → Import/Export → Import Calendar → Paste URL</li>
                <li><strong>VRBO:</strong> Calendar → Import Calendar → Paste URL</li>
              </ul>
            </div>
          </div>
        ) : (
          <button className="btn btn-primary" onClick={generateExportUrl}>
            <Calendar size={16} /> Generate Export URL
          </button>
        )}
      </div>

      {/* Import Calendars */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="card-title">📥 Import Calendars (From OTAs)</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={handleSyncNow}
              disabled={syncing || syncs.imports.length === 0}
            >
              <RefreshCw size={16} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
              Sync Now
            </button>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              <Plus size={16} /> Add Calendar
            </button>
          </div>
        </div>

        {syncs.imports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#9CA3AF' }}>
            <Calendar size={48} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
            <p>No calendar imports configured</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>Add calendar feeds from Airbnb, Booking.com, or VRBO to import bookings automatically</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {syncs.imports.map(feed => {
              const platform = PLATFORMS.find(p => p.value === feed.platform) || PLATFORMS[4];
              return (
                <div key={feed._id} className="card" style={{ padding: 14, background: '#F9FAFB' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                      <div style={{ fontSize: 24 }}>{platform.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                          {feed.name || platform.label}
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {feed.icalUrl}
                        </div>
                        {feed.lastSynced && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                            Last synced: {new Date(feed.lastSynced).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggleSync(feed._id, feed.syncEnabled)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        title={feed.syncEnabled ? 'Disable sync' : 'Enable sync'}
                      >
                        {feed.syncEnabled
                          ? <ToggleRight size={32} color="#10B981" />
                          : <ToggleLeft size={32} color="#9CA3AF" />
                        }
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleRemoveFeed(feed._id)}
                        style={{ padding: '6px 10px' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Feed Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Add Calendar Import</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddFeed}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Platform *</label>
                  <select
                    className="form-input"
                    value={newFeed.platform}
                    onChange={e => setNewFeed(p => ({ ...p, platform: e.target.value }))}
                    required
                  >
                    {PLATFORMS.map(platform => (
                      <option key={platform.value} value={platform.value}>
                        {platform.icon} {platform.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Name (optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., My Airbnb Listing"
                    value={newFeed.name}
                    onChange={e => setNewFeed(p => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">iCal URL *</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://..."
                    value={newFeed.icalUrl}
                    onChange={e => setNewFeed(p => ({ ...p, icalUrl: e.target.value }))}
                    required
                  />
                  <p style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                    Get this URL from your OTA platform's calendar export/sync settings
                  </p>
                </div>
                <div style={{ padding: 12, background: '#EFF6FF', borderRadius: 8, fontSize: 12, color: '#1E40AF' }}>
                  <strong>Where to find iCal URL:</strong>
                  <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                    <li><strong>Airbnb:</strong> Calendar → Availability Settings → Export Calendar</li>
                    <li><strong>Booking.com:</strong> Calendar → Sync Calendars → Export</li>
                    <li><strong>VRBO:</strong> Calendar → iCal Export</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Plus size={16} /> Add Calendar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
